/**
 * Purchase Request View Modal
 * Displays detailed purchase request information with actions, as a modal
 * (mirrors the Bill Payment / Normal Payment view-modal pattern) instead of
 * a routed full page.
 */

import {
  ArrowPathIcon,
  ChatBubbleLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  PrinterIcon,
  TrashIcon,
  XCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import DetailLink from '../../Common/DetailLink';
import { useAuth } from '../../../contexts/AuthContext';
import ProcurementDocuments from '../../Common/ProcurementDocuments';
import { inventoryAPI } from '../../../services/inventoryApi';
import { costCenterApi } from '../../../services/financeApi';
import adminAPI from '../../../services/adminApi';
import ConfirmationModal from '../../Common/ConfirmationModal';
import CustomSelect from '../../Common/CustomSelect';
import LoadingSpinner from '../../Common/LoadingSpinner';
import PermissionGuard from '../../Common/PermissionGuard';
import TextInput from '../../Common/TextInput';
import PurchaseRequestQuotations from '../Quotations/PurchaseRequestQuotations';
import { openPurchaseRequestReport } from '../../../utils/openInventoryReport';
import { canPrintPurchaseRequest } from '../../../utils/documentPrintGate';

const PurchaseRequestView = ({ isOpen, onClose, purchaseRequestId, onEdit, onRefresh }) => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const [purchaseRequest, setPurchaseRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({});
  const [approvalData, setApprovalData] = useState({ comments: '' });
  const [rejectionData, setRejectionData] = useState({ reason: '', comments: '' });
  const [noteData, setNoteData] = useState({
    note: '',
    category: 'general',
    isPrivate: false
  });
  const [costCenterName, setCostCenterName] = useState('');

  // Cost center is stored as an id; prefer the stored label and fall back to a lookup
  // for requests created before the label was captured.
  useEffect(() => {
    if (!purchaseRequest?.costCenter) { setCostCenterName(''); return; }
    if (purchaseRequest.costCenterLabel) { setCostCenterName(purchaseRequest.costCenterLabel); return; }
    costCenterApi.getCostCenterById(purchaseRequest.costCenter)
      .then(res => {
        const cc = res.data?.data?.costCenter || res.data?.data || res.data;
        setCostCenterName(cc ? `${cc.code} - ${cc.name}` : purchaseRequest.costCenter);
      })
      .catch(() => setCostCenterName(purchaseRequest.costCenter));
  }, [purchaseRequest?.costCenter, purchaseRequest?.costCenterLabel]);

  useEffect(() => {
    if (!isOpen || !purchaseRequestId) return;
    loadPurchaseRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, purchaseRequestId]);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const locationId = localStorage.getItem('X-Location-Id') || localStorage.getItem('lastUsedLocationId');
        if (!locationId) return;
        const res = await adminAPI.getLocationById(locationId);
        const loc = res.data?.data || res.data || {};
        const addr = loc.address || {};
        setCompanyInfo({
          name: loc.name || loc.locationName || '',
          street: addr.street || addr.addressLine1 || '',
          city: addr.city || '',
          zipCode: addr.zipCode || addr.postalCode || '',
        });
      } catch (e) {
        console.warn('[PurchaseRequestView] could not load location info:', e.message);
      }
    })();
  }, [isOpen]);

  const loadPurchaseRequest = async () => {
    try {
      setLoading(true);
      const response = await inventoryAPI.getPurchaseRequestById(purchaseRequestId);
      setPurchaseRequest(response.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load purchase request');
      console.error('Load purchase request error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    await openPurchaseRequestReport(purchaseRequest, companyInfo);
  };

  const handleEdit = () => {
    if (!['draft', 'submitted', 'pending_approval'].includes(purchaseRequest.status)) {
      toast.error('Purchase request cannot be edited in its current status');
      return;
    }
    onEdit?.(purchaseRequest);
  };

  const handleDelete = () => {
    if (['approved', 'converted_to_po'].includes(purchaseRequest.status)) {
      toast.error('Cannot delete approved or converted purchase requests');
      return;
    }

    setConfirmAction({
      title: 'Delete Purchase Request',
      message: `Are you sure you want to delete purchase request ${purchaseRequest.prNumber}? This action cannot be undone.`,
      confirmText: 'Delete',
      type: 'danger',
      onConfirm: async () => {
        try {
          await inventoryAPI.deletePurchaseRequest(purchaseRequestId);
          toast.success('Purchase request deleted successfully');
          onRefresh?.();
          onClose();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete purchase request');
        } finally {
          setConfirmAction(null);
        }
      }
    });
  };

  const handleSubmitForApproval = async () => {
    if (purchaseRequest.status !== 'draft') {
      toast.error('Only draft requests can be submitted for approval');
      return;
    }

    try {
      setActionLoading('submit');
      await inventoryAPI.submitPurchaseRequestForApproval(purchaseRequestId);
      toast.success('Purchase request submitted for approval');
      await loadPurchaseRequest();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit purchase request');
    } finally {
      setActionLoading('');
    }
  };

  const handleApprove = async () => {
    try {
      setActionLoading('approve');
      await inventoryAPI.approvePurchaseRequest(purchaseRequestId, approvalData);
      toast.success('Purchase request approved successfully');
      setShowApprovalModal(false);
      await loadPurchaseRequest();
      setApprovalData({ comments: '' });
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve purchase request');
    } finally {
      setActionLoading('');
    }
  };

  const handleReject = async () => {
    try {
      setActionLoading('reject');
      await inventoryAPI.rejectPurchaseRequest(purchaseRequestId, rejectionData);
      toast.success('Purchase request rejected');
      setShowRejectionModal(false);
      setRejectionData({ reason: '', comments: '' });
      await loadPurchaseRequest();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject purchase request');
    } finally {
      setActionLoading('');
    }
  };

  // Convert opens the purchase order form pre-loaded with this request, mirroring how
  // "Convert to GRN" hands a purchase order to the GRN form.
  const handleConvertToPO = () => {
    onClose?.();
    navigate('/inventory/purchase-orders', {
      state: {
        selectedPRId: purchaseRequestId,
        fromPR: true,
        from: '/inventory/purchase-requests'
      }
    });
  };

  const handleAddNote = async () => {
    if (!noteData.note.trim()) {
      toast.error('Note content is required');
      return;
    }

    try {
      setActionLoading('note');
      await inventoryAPI.addPurchaseRequestNote(purchaseRequestId, noteData);
      toast.success('Note added successfully');
      setShowNoteModal(false);
      setNoteData({ note: '', category: 'general', isPrivate: false });
      await loadPurchaseRequest();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add note');
    } finally {
      setActionLoading('');
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      submitted: 'bg-blue-100 text-blue-800 border-blue-200',
      pending_approval: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
      converted_to_po: 'bg-purple-100 text-purple-800 border-purple-200',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPriorityBadgeColor = (priority) => {
    const colors = {
      normal: 'bg-green-100 text-green-800 border-green-200',
      urgent: 'bg-orange-100 text-orange-800 border-orange-200',
      critical: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LKR'
    }).format(amount);
  };

  const canApprove = () => {
    return hasPermission('INVENTORY:PURCHASE_REQUESTS:APPROVE') &&
           ['submitted', 'pending_approval'].includes(purchaseRequest?.status);
  };

  const canConvert = () => {
    return hasPermission('INVENTORY:PURCHASE_REQUESTS:CONVERT_TO_PO') &&
           purchaseRequest?.status === 'approved' &&
           !!purchaseRequest?.selectedQuotation; // raised from the selected supplier quotation
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/30 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-white/20 rounded flex items-center justify-center">
              <DocumentIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{purchaseRequest?.prNumber || 'Purchase Request'}</h3>
              {purchaseRequest && (
                <p className="text-sm text-indigo-100">Created on {formatDate(purchaseRequest.createdAt)}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-10 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="mt-2 text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : !purchaseRequest ? (
            <div className="p-10 text-center">
              <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Purchase request not found</h3>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* Status & Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadgeColor(purchaseRequest.status)}`}>
                    {purchaseRequest.status?.replace(/_/g, ' ')}
                  </span>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-indigo-600">{formatCurrency(purchaseRequest.totalEstimatedAmount)}</p>
                    <p className="text-xs text-gray-500">Total Estimate</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Priority</p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityBadgeColor(purchaseRequest.priority)}`}>
                      {purchaseRequest.priority}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-500">Request Date</p>
                    <p className="font-medium">{formatDate(purchaseRequest.requestDate)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Items</p>
                    <p className="font-medium">{purchaseRequest.lineItems?.length || 0}</p>
                  </div>
                  {purchaseRequest.daysUntilRequired !== null && purchaseRequest.daysUntilRequired !== undefined && (
                    <div>
                      <p className="text-gray-500">Days Until Required</p>
                      <p className={`font-medium ${
                        purchaseRequest.daysUntilRequired < 0 ? 'text-red-600' :
                        purchaseRequest.daysUntilRequired < 7 ? 'text-orange-600' :
                        'text-green-600'
                      }`}>
                        {purchaseRequest.daysUntilRequired < 0
                          ? `${Math.abs(purchaseRequest.daysUntilRequired)} days overdue`
                          : `${purchaseRequest.daysUntilRequired} days`}
                      </p>
                    </div>
                  )}
                  {purchaseRequest.costCenter && (
                    <div>
                      <p className="text-gray-500">Cost Center</p>
                      <p className="font-medium">{costCenterName || purchaseRequest.costCenter}</p>
                    </div>
                  )}
                </div>

                {(purchaseRequest.createdByUserName || purchaseRequest.updatedByUserName || purchaseRequest.approvedByUserName) && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm mt-3 pt-3 border-t border-gray-200">
                    {purchaseRequest.createdByUserName && (
                      <div>
                        <p className="text-gray-500">Created By</p>
                        <p className="font-medium">{purchaseRequest.createdByUserName}</p>
                        <p className="text-xs text-gray-500">{formatDate(purchaseRequest.createdAt)}</p>
                      </div>
                    )}
                    {purchaseRequest.updatedByUserName && (
                      <div>
                        <p className="text-gray-500">Last Updated By</p>
                        <p className="font-medium">{purchaseRequest.updatedByUserName}</p>
                        <p className="text-xs text-gray-500">{formatDate(purchaseRequest.updatedAt)}</p>
                      </div>
                    )}
                    {purchaseRequest.approvedByUserName && (
                      <div>
                        <p className="text-gray-500">Approved By</p>
                        <p className="font-medium">{purchaseRequest.approvedByUserName}</p>
                        <p className="text-xs text-gray-500">{formatDate(purchaseRequest.approvedAt)}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Converted Purchase Order banner */}
              {purchaseRequest.purchaseOrder && (
                <div className="bg-indigo-50 rounded-lg p-3 flex items-center justify-between">
                  <span className="text-sm text-indigo-900">This request has been converted to a Purchase Order.</span>
                  <DetailLink
                    to={`/inventory/purchase-orders/${purchaseRequest.purchaseOrder._id || purchaseRequest.purchaseOrder}`}
                    className="text-sm font-medium text-indigo-700 hover:text-indigo-900 whitespace-nowrap ml-3"
                  >
                    View Purchase Order &rarr;
                  </DetailLink>
                </div>
              )}

              {/* Line Items */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                  <DocumentIcon className="w-4 h-4 mr-1" />
                  Line Items ({purchaseRequest.lineItems?.length || 0})
                </h4>
                <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Line #</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty/UoM</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{purchaseRequest?.procurementType === 'SERVICE' ? 'Service Required By' : 'Required By'}</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Estimated Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {purchaseRequest.lineItems?.map((item) => (
                        <tr key={item.lineNo} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-900">{item.lineNo}</td>
                          <td className="px-3 py-2">
                            <div className="text-gray-900">{item.itemDescription}</div>
                            {item.itemCode && (
                              <div className="text-xs text-gray-500">Code: {item.itemCode}</div>
                            )}
                            {item.specifications && (
                              <div className="text-xs text-gray-500 mt-0.5">Specs: {item.specifications}</div>
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                            {item.quantityRequested} {item.uom}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                            {new Date(item.requiredByDate).toLocaleDateString()}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="text-gray-900">
                              {item.estimatedTotalPrice ? formatCurrency(item.estimatedTotalPrice) : 'N/A'}
                            </div>
                            {item.estimatedUnitPrice && (
                              <div className="text-xs text-gray-500">Unit: {formatCurrency(item.estimatedUnitPrice)}</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {purchaseRequest.totalEstimatedAmount ? (
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td className="px-3 py-2 text-sm font-medium text-gray-900" colSpan="4">Total Estimate</td>
                          <td className="px-3 py-2 text-right font-bold text-indigo-600">
                            {formatCurrency(purchaseRequest.totalEstimatedAmount)}
                          </td>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
              </div>

              {/* Supplier quotations (approved requests) */}
              <PurchaseRequestQuotations purchaseRequest={purchaseRequest} />

              {/* Budget Information */}
              {(purchaseRequest.budgetInfo?.budgetCode || purchaseRequest.budgetInfo?.budgetAmount) && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-purple-900 mb-2">Budget Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {purchaseRequest.budgetInfo.budgetCode && (
                      <p className="text-purple-700">Code: <span className="font-medium text-purple-900">{purchaseRequest.budgetInfo.budgetCode}</span></p>
                    )}
                    {purchaseRequest.budgetInfo.budgetAmount && (
                      <p className="text-purple-700">Amount: <span className="font-medium text-purple-900">{formatCurrency(purchaseRequest.budgetInfo.budgetAmount)}</span></p>
                    )}
                  </div>
                </div>
              )}

              {/* Approval Workflow */}
              {purchaseRequest.approvalWorkflow && purchaseRequest.approvalWorkflow.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Approval Workflow</h4>
                  <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
                    {purchaseRequest.approvalWorkflow.map((approval, index) => (
                      <div key={index} className="p-3 flex items-center space-x-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          approval.status === 'approved' ? 'bg-green-100 text-green-600' :
                          approval.status === 'rejected' ? 'bg-red-100 text-red-600' :
                          approval.status === 'pending' ? 'bg-yellow-100 text-yellow-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {approval.status === 'approved' ? (
                            <CheckCircleIcon className="w-4 h-4" />
                          ) : approval.status === 'rejected' ? (
                            <XCircleIcon className="w-4 h-4" />
                          ) : (
                            <ClockIcon className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            Level {approval.level} &mdash; {approval.approver?.name || 'Unknown'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {approval.status === 'pending' ? 'Pending approval' :
                             approval.approvedAt ? formatDate(approval.approvedAt) :
                             'Not processed'}
                          </p>
                          {approval.comments && (
                            <p className="text-xs text-gray-600 mt-0.5">&ldquo;{approval.comments}&rdquo;</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Supporting documents */}
              <ProcurementDocuments docType="purchase-requests" docId={purchaseRequest._id}
                canEdit={hasPermission('INVENTORY:PURCHASE_REQUESTS:UPDATE') || hasPermission('INVENTORY:PURCHASE_REQUESTS:CREATE')} />

              {/* Internal Notes */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                    <ChatBubbleLeftIcon className="w-4 h-4 mr-1" />
                    Internal Notes {purchaseRequest.internalNotes?.length ? `(${purchaseRequest.internalNotes.length})` : ''}
                  </h4>
                  <PermissionGuard permission="INVENTORY:PURCHASE_REQUESTS:UPDATE">
                    <button
                      onClick={() => setShowNoteModal(true)}
                      className="text-sm text-indigo-600 hover:text-indigo-800"
                    >
                      Add Note
                    </button>
                  </PermissionGuard>
                </div>
                {purchaseRequest.internalNotes && purchaseRequest.internalNotes.length > 0 ? (
                  <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
                    {purchaseRequest.internalNotes.map((note, index) => (
                      <div key={index} className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-gray-900">
                            {note.createdBy?.name || 'Unknown'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDate(note.createdAt)}
                          </div>
                        </div>
                        <p className="mt-1 text-sm text-gray-700">{note.note}</p>
                        {note.category !== 'general' && (
                          <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                            {note.category}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 bg-gray-50 rounded-lg p-3">No notes yet.</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons Footer */}
        {purchaseRequest && (
          <div className="bg-gray-50 px-4 py-3 flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>

            <div className="flex flex-wrap items-center justify-end gap-2">
              {purchaseRequest.status === 'draft' && (
                <PermissionGuard permission="INVENTORY:PURCHASE_REQUESTS:UPDATE">
                  <button
                    onClick={handleSubmitForApproval}
                    disabled={actionLoading === 'submit'}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {actionLoading === 'submit' ? (
                      <ArrowPathIcon className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <CheckCircleIcon className="w-4 h-4 mr-1" />
                    )}
                    Submit for Approval
                  </button>
                </PermissionGuard>
              )}

              {canApprove() && (
                <>
                  <button
                    onClick={() => setShowApprovalModal(true)}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                    Approve
                  </button>
                  <button
                    onClick={() => setShowRejectionModal(true)}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                  >
                    <XCircleIcon className="w-4 h-4 mr-1" />
                    Reject
                  </button>
                </>
              )}

              {purchaseRequest.status === 'approved' && !purchaseRequest.selectedQuotation && hasPermission('INVENTORY:PURCHASE_REQUESTS:CONVERT_TO_PO') && (
                <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="convert-hint">
                  Select a supplier quotation (Supplier Quotations → Analyse) to create the PO
                </span>
              )}

              {canConvert() && (
                <button
                  onClick={handleConvertToPO}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                >
                  <ArrowPathIcon className="w-4 h-4 mr-1" />
                  Convert to PO
                </button>
              )}

              {canPrintPurchaseRequest(purchaseRequest.status) && (
                <button
                  onClick={handlePrint}
                  title="Print PDF"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PrinterIcon className="w-4 h-4 mr-1" />
                  Print PDF
                </button>
              )}

              {['draft', 'submitted', 'pending_approval'].includes(purchaseRequest.status) && <PermissionGuard permission="INVENTORY:PURCHASE_REQUESTS:UPDATE">
                <button
                  onClick={handleEdit}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PencilIcon className="w-4 h-4 mr-1" />
                  Edit
                </button>
              </PermissionGuard>}

              {!['approved', 'converted_to_po'].includes(purchaseRequest.status) && <PermissionGuard permission="INVENTORY:PURCHASE_REQUESTS:DELETE">
                <button
                  onClick={handleDelete}
                  className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                >
                  <TrashIcon className="w-4 h-4 mr-1" />
                  Delete
                </button>
              </PermissionGuard>}
            </div>
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white p-5 border w-96 shadow-lg rounded-md">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Approve Purchase Request</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments (optional)
                </label>
                <TextInput
                  variant="textarea"
                  value={approvalData.comments}
                  onChange={(e) => setApprovalData({ ...approvalData, comments: e.target.value })}
                  placeholder="Add approval comments..."
                  rows={3}
                />
              </div>
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowApprovalModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  disabled={actionLoading === 'approve'}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50"
                >
                  {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white p-5 border w-96 shadow-lg rounded-md">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Purchase Request</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason *
                </label>
                <TextInput
                  variant="textarea"
                  value={rejectionData.reason}
                  onChange={(e) => setRejectionData({ ...rejectionData, reason: e.target.value })}
                  placeholder="Please provide a reason for rejection..."
                  rows={3}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Comments
                </label>
                <TextInput
                  variant="textarea"
                  value={rejectionData.comments}
                  onChange={(e) => setRejectionData({ ...rejectionData, comments: e.target.value })}
                  placeholder="Add additional comments..."
                  rows={2}
                />
              </div>
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowRejectionModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={actionLoading === 'reject' || !rejectionData.reason.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                >
                  {actionLoading === 'reject' ? 'Rejecting...' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white p-5 border w-96 shadow-lg rounded-md">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Internal Note</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note *
                  </label>
                  <TextInput
                    variant="textarea"
                    value={noteData.note}
                    onChange={(e) => setNoteData({ ...noteData, note: e.target.value })}
                    placeholder="Enter note content..."
                    rows={4}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <CustomSelect
                    value={noteData.category}
                    onChange={(e) => setNoteData({ ...noteData, category: e.target.value })}
                    options={[
                      { value: 'general', label: 'General' },
                      { value: 'approval', label: 'Approval' },
                      { value: 'procurement', label: 'Procurement' },
                      { value: 'budget', label: 'Budget' },
                      { value: 'urgent', label: 'Urgent' },
                      { value: 'follow_up', label: 'Follow Up' }
                    ]}
                  />
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={noteData.isPrivate}
                      onChange={(e) => setNoteData({ ...noteData, isPrivate: e.target.checked })}
                      className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Private note</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowNoteModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddNote}
                  disabled={actionLoading === 'note' || !noteData.note.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50"
                >
                  {actionLoading === 'note' ? 'Adding...' : 'Add Note'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <ConfirmationModal
          isOpen={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          onConfirm={confirmAction.onConfirm}
          title={confirmAction.title}
          message={confirmAction.message}
          confirmText={confirmAction.confirmText}
          type={confirmAction.type}
        />
      )}
    </div>
  );
};

export default PurchaseRequestView;
